import { useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { CoachInsight } from '@/components/coach/coachInsightTypes';

const STALE_ANALYSIS_DAYS = 14;
const STALLED_GOAL_DAYS = 14;
const VALUES_THRESHOLD = 3;

interface AnalysisRow {
  analysis_type: string;
  updated_at: string;
}

interface GoalRow {
  id: string;
  title: string;
  progress: number;
  updated_at: string;
  archived: boolean;
}

interface HabitRow {
  id: string;
  title: string;
  streak: number;
}

interface MentorConnectionRow {
  id: string;
  status: string;
  mentor_id: string;
  mentee_id: string;
}

export function useCoachInsights() {
  const { user } = useAuth();
  const { profile, isLoading: profileLoading } = useProfile();

  // Fetch core values count (for onboarding state)
  const { data: valuesCount } = useQuery<number>({
    queryKey: ['coach-insights-values', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { count } = await supabase
        .from('core_values')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);
      return count ?? 0;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch analyses (non-blocking)
  const { data: analyses } = useQuery<AnalysisRow[]>({
    queryKey: ['coach-insights-analyses', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data } = await supabase
        .from('career_coach_analyses')
        .select('analysis_type, updated_at')
        .eq('user_id', user.id);
      return data ?? [];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch active goals (non-blocking)
  const { data: goals } = useQuery<GoalRow[]>({
    queryKey: ['coach-insights-goals', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data } = await supabase
        .from('goals')
        .select('id, title, progress, updated_at, archived')
        .eq('user_id', user.id)
        .eq('archived', false);
      return data ?? [];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch habits via goals join (habits has no user_id column)
  const { data: habits } = useQuery<HabitRow[]>({
    queryKey: ['coach-insights-habits', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data } = await supabase
        .from('habits')
        .select('id, title, streak, goal:goals!inner(user_id)')
        .eq('goals.user_id', user.id);
      return (data ?? []).map(h => ({ id: h.id, title: h.title, streak: h.streak }));
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch pending mentor requests (non-blocking)
  const { data: pendingRequests } = useQuery<MentorConnectionRow[]>({
    queryKey: ['coach-insights-pending-requests', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data } = await supabase
        .from('mentorship_connections')
        .select('id, status, mentor_id, mentee_id')
        .eq('mentee_id', user.id)
        .eq('status', 'pending');
      return data ?? [];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // Check if user has a mentor profile (non-blocking)
  const { data: mentorProfile } = useQuery({
    queryKey: ['coach-insights-mentor-profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from('mentor_profiles')
        .select('id, is_seeking_mentor, skills_want_to_learn')
        .eq('user_id', user.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const insights = useMemo<CoachInsight[]>(() => {
    // Wait for values count to load before generating any insights
    if (valuesCount === undefined) return [];

    const result: CoachInsight[] = [];
    const now = Date.now();
    const hasValues = valuesCount >= VALUES_THRESHOLD;
    const hasGoals = goals && goals.length > 0;
    const hasHabits = habits && habits.length > 0;

    // Progressive onboarding states (persistent — always visible)
    if (!hasValues) {
      result.push({
        type: 'onboarding_locked',
        message: 'Complete Values Discovery to unlock your AI Coach',
        action: { label: 'Discover Values', tab: 'values' },
        priority: -2,
        persistent: false,
      });
      return result;
    }

    if (!hasGoals || !hasHabits) {
      const missing: string[] = [];
      if (!hasGoals) missing.push('a goal');
      if (!hasHabits) missing.push('a habit');
      result.push({
        type: 'onboarding_unlocking',
        message: `Almost there! Create ${missing.join(' and ')} to activate your AI Coach`,
        action: { label: hasGoals ? 'Add Habit' : 'Set Goal', tab: hasGoals ? 'habits' : 'goals' },
        priority: -1,
        persistent: false,
      });
      return result;
    }

    // Active state — user has values + goals + habits

    // Returning user welcome (has habits with streaks > 0)
    if (hasHabits) {
      const maxStreak = Math.max(...habits!.map(h => h.streak));
      if (maxStreak > 0) {
        const bestHabit = habits!.find(h => h.streak === maxStreak);
        result.push({
          type: 'returning_welcome',
          message: maxStreak === 1
            ? `Welcome back! Keep "${bestHabit?.title}" going today`
            : `Day ${maxStreak} streak on "${bestHabit?.title}" — keep the momentum!`,
          action: { label: 'Check In', tab: 'habits' },
          priority: 0,
        });
      }
    }

    // First-time coaching: no analyses at all
    if (analyses && analyses.length === 0) {
      result.push({
        type: 'first_time',
        message: 'Ready for your first AI coaching session?',
        action: { label: 'Start Coaching', tab: 'coach_panel' },
        priority: 0,
      });
      // Still show other insights alongside first_time for active users
    }

    // Enhanced coaching upsell — high priority for unprompted users
    const enhancedCoachingPrompted = (profile as Record<string, unknown> | null)?.enhanced_coaching_prompted === true;
    const enhancedCoachingEnabled = (profile as Record<string, unknown> | null)?.enhanced_coaching_enabled === true;
    if (!profileLoading && !enhancedCoachingPrompted && !enhancedCoachingEnabled) {
      result.push({
        type: 'enhanced_coaching',
        message: 'Unlock personalized coaching — your AI coach can learn from your history for better insights.',
        action: { label: 'Enable', tab: 'enhanced_coaching' },
        priority: 0.5,
      });
    }

    // Stale analysis: most recent analysis > 14 days old
    if (analyses && analyses.length > 0) {
      const mostRecent = analyses.reduce((latest, a) =>
        new Date(a.updated_at) > new Date(latest.updated_at) ? a : latest
      );
      const daysSince = (now - new Date(mostRecent.updated_at).getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince > STALE_ANALYSIS_DAYS) {
        result.push({
          type: 'stale_analysis',
          message: 'Your coach analysis is outdated. Refresh for updated insights.',
          action: { label: 'Refresh Analysis', tab: 'coach_panel' },
          priority: 2,
        });
      }
    }

    // Broken streak: streak is 0 (habits table only has current streak)
    if (habits && habits.length > 0) {
      const brokenStreak = habits.find(h => h.streak === 0);
      if (brokenStreak) {
        result.push({
          type: 'broken_streak',
          message: `Your "${brokenStreak.title}" streak broke. Want to discuss strategies?`,
          action: { label: 'Get Advice', tab: 'coach_panel' },
          priority: 1,
        });
      }
    }

    // Stalled goal: goal not updated in 14+ days with < 100% progress
    if (goals) {
      const stalledGoal = goals.find(g => {
        const daysSinceUpdate = (now - new Date(g.updated_at).getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceUpdate > STALLED_GOAL_DAYS && g.progress < 100;
      });
      if (stalledGoal) {
        result.push({
          type: 'stalled_goal',
          message: `"${stalledGoal.title}" hasn't progressed in 14+ days. Need help breaking it down?`,
          action: { label: 'Break It Down', tab: 'coach_panel' },
          priority: 3,
        });
      }
    }

    // Pending mentor requests
    if (pendingRequests && pendingRequests.length > 0) {
      result.push({
        type: 'pending_mentor_request',
        message: `You have ${pendingRequests.length} pending mentor request${pendingRequests.length === 1 ? '' : 's'}`,
        action: { label: 'Review', tab: 'mentorship' },
        priority: 1,
      });
    }

    // No mentor profile (only suggest if user has 2+ goals)
    if (!mentorProfile && goals && goals.length >= 2) {
      result.push({
        type: 'no_mentor_profile',
        message: 'Set up your mentor profile to find mentors aligned with your goals.',
        action: { label: 'Set Up Profile', tab: 'mentorship' },
        priority: 5,
      });
    }

    // Mentor match available (if seeking + has desired skills)
    if (mentorProfile && (mentorProfile as Record<string, unknown>).is_seeking_mentor &&
        Array.isArray((mentorProfile as Record<string, unknown>).skills_want_to_learn) &&
        ((mentorProfile as Record<string, unknown>).skills_want_to_learn as string[]).length > 0) {
      result.push({
        type: 'mentor_match_available',
        message: 'AI-powered mentor matching can find mentors aligned with your career goals.',
        action: { label: 'Find Mentors', tab: 'mentorship' },
        priority: 4,
      });
    }

    return result.sort((a, b) => a.priority - b.priority);
  }, [valuesCount, analyses, goals, habits, pendingRequests, mentorProfile, profile, profileLoading]);

  return { insights, topInsight: insights[0] ?? null };
}
